/* Highlights Ticker 🛠️ — sections/custom-highlights-ticker.liquid
 * Scrolls the track by one copy of the list in a seamless loop at a constant speed (px/s),
 * however many highlights there are. The scroll only starts once the list has been measured
 * (after fonts load), and if its width changes later the speed is updated without a jump. */

if (!customElements.get('highlights-ticker')) {
  const reduceMotion = matchMedia('(prefers-reduced-motion: reduce)');

  class HighlightsTicker extends HTMLElement {
    // connected/disconnected also run when the theme editor re-renders the section.
    connectedCallback() {
      this.track = this.querySelector('.highlights-ticker__track');
      this.group = this.querySelector('.highlights-ticker__group');
      if (!this.track || !this.group || reduceMotion.matches) return;

      // Wait for the theme fonts so the first measurement is the real width.
      (document.fonts?.ready ?? Promise.resolve()).then(() => {
        if (!this.isConnected) return;
        this.start();
        this.resizeObserver = new ResizeObserver(() => this.updateSpeed());
        this.resizeObserver.observe(this.group);
      });
    }

    duration() {
      const speed = Number(this.dataset.speed) || 50; // px per second
      return (this.group.getBoundingClientRect().width / speed) * 1000; // ms to move one copy
    }

    start() {
      const duration = this.duration();
      if (!duration) return;
      this.animation = this.track.animate(
        [{ transform: 'translateX(0)' }, { transform: 'translateX(-50%)' }],
        { duration, iterations: Infinity, easing: 'linear' }
      );
    }

    // Keep the current position (as a fraction of the loop) when the duration changes.
    updateSpeed() {
      if (!this.animation) return this.start();
      const previous = this.animation.effect.getTiming().duration;
      const next = this.duration();
      if (!next || Math.abs(next - previous) < 1) return;
      const progress = ((this.animation.currentTime ?? 0) % previous) / previous;
      this.animation.effect.updateTiming({ duration: next });
      this.animation.currentTime = progress * next;
    }

    disconnectedCallback() {
      this.resizeObserver?.disconnect();
      this.animation?.cancel();
    }
  }

  customElements.define('highlights-ticker', HighlightsTicker);
}
